import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { VeracordLogo } from '@/components/ui/VeracordLogo';
import { ArrowRight, LogIn, Menu, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface AppNavPublicProps {
  activeItem?: string | null;
  fixed?: boolean;
  scrolled?: boolean;
}

const NAV_ITEMS = [
  { label: 'How It Works', anchor: '#how-it-works' },
  { label: 'FAQ', path: '/faq' },
];

export const AppNavPublic: React.FC<AppNavPublicProps> = ({
  activeItem = null,
  fixed = false,
  scrolled = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navStyle: React.CSSProperties = fixed
    ? {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: scrolled || mobileOpen ? 'rgba(0,2,46,0.92)' : 'transparent',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255,255,255,0.2)',
        transition: 'background 0.3s',
      }
    : {
        position: 'relative' as const,
        zIndex: 50,
      };

  const handleHowItWorks = () => {
    setMobileOpen(false);
    if (location.pathname === '/') {
      document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/?scrollTo=how-it-works');
    }
  };

  return (
    <>
      <nav className="w-full" style={navStyle}>
        <div className="max-w-7xl mx-auto w-full flex items-center justify-between px-6 md:px-12 py-4 md:py-6">
          {/* Logo + desktop nav links */}
          <div className="flex items-center gap-10">
            <Link to="/" className="flex items-center text-white no-underline">
              <VeracordLogo size="md" />
            </Link>

            {/* Desktop nav links */}
            <div className="hidden md:flex items-center gap-8 text-sm font-medium">
              {NAV_ITEMS.map(item => {
                const isActive = activeItem === item.label;
                if (item.anchor) {
                  return (
                    <a
                      key={item.label}
                      href={item.anchor}
                      onClick={(e) => { e.preventDefault(); handleHowItWorks(); }}
                      className={`relative px-1 py-1 transition-colors hover:text-white/80 cursor-pointer ${isActive ? 'text-white' : 'text-white/60'}`}
                    >
                      {item.label}
                      {isActive && (
                        <motion.div layoutId="publicNavUnderline" className="absolute -bottom-1 left-0 right-0 h-0.5" style={{ backgroundColor: '#339999' }} />
                      )}
                    </a>
                  );
                }
                return (
                  <Link
                    key={item.label}
                    to={item.path!}
                    className={`relative px-1 py-1 transition-colors hover:text-white/80 no-underline ${isActive ? 'text-white' : 'text-white/60'}`}
                  >
                    {item.label}
                    {isActive && (
                      <motion.div layoutId="publicNavUnderline" className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full" style={{ backgroundColor: '#339999' }} />
                    )}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Desktop CTA buttons */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/auth"
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg text-white no-underline transition-all hover:bg-white/5"
              style={{ border: '1px solid rgba(255,255,255,0.2)' }}
            >
              <LogIn className="w-4 h-4" />
              Log In
            </Link>
            <Link
              to="/get-started"
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white rounded-lg transition-all no-underline"
              style={{ backgroundColor: '#339999', boxShadow: '0 0 20px rgba(51,153,153,0.3)' }}
            >
              Get Started
              <motion.span animate={{ x: [0, 5, 0] }} transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }} className="inline-flex">
                <ArrowRight className="w-4 h-4" />
              </motion.span>
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden flex items-center justify-center w-10 h-10 text-white rounded-lg transition-colors"
            style={mobileOpen ? { background: 'rgba(255,255,255,0.12)', borderRadius: '8px' } : {}}
            onClick={() => setMobileOpen(prev => !prev)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile dropdown menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
              className="md:hidden flex flex-col"
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                height: '100vh',
                background: 'rgba(0,2,46,0.98)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                zIndex: 99,
              }}
            >
              {/* Re-render nav bar row at top of overlay */}
              <div className="max-w-7xl mx-auto w-full flex items-center justify-between px-6 md:px-12 py-4 md:py-6">
                <Link to="/" className="flex items-center text-white no-underline">
                  <VeracordLogo size="md" />
                </Link>
                <button
                  className="flex items-center justify-center w-10 h-10 text-white rounded-lg transition-colors"
                  style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '8px' }}
                  onClick={() => setMobileOpen(false)}
                  aria-label="Close menu"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Menu items */}
              <div className="px-6 pt-2 flex-1">
                <button
                  onClick={handleHowItWorks}
                  className="w-full text-left py-4 text-lg font-medium text-white/90 hover:text-white transition-colors"
                >
                  How It Works
                </button>
                <div className="h-px bg-white/10" />
                <Link
                  to="/faq"
                  onClick={() => setMobileOpen(false)}
                  className="block py-4 text-lg font-medium text-white/90 hover:text-white transition-colors no-underline"
                >
                  FAQ
                </Link>
                <div className="h-px bg-white/10" />
                <Link
                  to="/auth"
                  onClick={() => setMobileOpen(false)}
                  className="block py-4 text-lg font-medium text-white/90 hover:text-white transition-colors no-underline"
                >
                  Log In
                </Link>
              </div>

              {/* Get Started pinned to bottom naturally */}
              <div style={{ padding: 24 }}>
                <Link
                  to="/get-started"
                  onClick={() => setMobileOpen(false)}
                  className="w-full flex items-center justify-center gap-2 font-bold text-white transition-all no-underline"
                  style={{
                    padding: '12px 24px',
                    fontSize: 15,
                    borderRadius: 10,
                    backgroundColor: '#339999',
                    boxShadow: '0 0 20px rgba(51,153,153,0.3)',
                  }}
                >
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Nav divider */}
      {!fixed && (
        <div className="w-full h-px bg-white/20" style={{ position: 'relative', zIndex: 50 }} />
      )}
    </>
  );
};

export default AppNavPublic;
